import { Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import type { ContextGap } from '@/types/api'

interface GapPreFillCardsProps {
  gap: ContextGap
  /** Confirm a single pre-fill card. Page wires this to the message
   * pipeline so the assistant can persist the value. */
  onConfirm: (gapId: string, field: string, value: string) => void
}

/**
 * Pre-fill card list for a single gap. Backend `gap.preFill[]` is a P1
 * field still in flight (see [docs/VIDIT-QUESTIONS-2026-04-19.md]); when
 * absent or empty, the component renders nothing — silent fallback to
 * "guided chat only" per FRONTEND-SPEC §Screen 3 / DESIGN.md decision.
 *
 * The "Edit first" button is intentionally absent in v1 — the user can
 * always reply in the chat thread to refine. Keeping the surface tight
 * avoids a half-built editor experience while preFill is still a moving
 * target on the backend.
 */
export function GapPreFillCards({ gap, onConfirm }: GapPreFillCardsProps) {
  const cards = (gap.preFill ?? []).filter((c) => c && c.field)
  if (cards.length === 0) return null

  return (
    <div
      data-testid={`gap-prefill-${gap.id}`}
      className="flex flex-col gap-2"
    >
      {cards.map((card, i) => (
        <article
          key={`${card.field}-${i}`}
          className="rounded-md border border-border-default bg-bg-elevated px-4 py-3"
        >
          <div className="flex items-center gap-1.5 text-xs text-accent font-mono mb-1.5">
            <Sparkles size={12} />
            {humanizeField(card.field)}
          </div>
          <p className="text-sm text-text-primary leading-relaxed mb-3 whitespace-pre-wrap break-words">
            {valueToString(card.value)}
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() =>
                onConfirm(gap.id, card.field, valueToString(card.value))
              }
              data-testid={`gap-prefill-confirm-${card.field}`}
            >
              Confirm
            </Button>
            {typeof card.confidence === 'number' && (
              <span className="text-xs text-text-tertiary font-mono">
                {Math.round(card.confidence * 100)}% confidence
              </span>
            )}
          </div>
        </article>
      ))}
    </div>
  )
}

function humanizeField(field: string): string {
  return field
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase())
}

function valueToString(value: unknown): string {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return value.filter(Boolean).join(', ')
  if (value && typeof value === 'object') return JSON.stringify(value)
  return String(value ?? '')
}
